import { useEffect, useState } from "react";
import { adminHttp } from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Loader2, Plus, X } from "lucide-react";

const SECTIONS = [
  { key: "hero", label: "Hero" },
  { key: "home_about", label: "Home · Vision" },
  { key: "market", label: "Market Insights" },
  { key: "contact", label: "Contact" },
  { key: "footer", label: "Footer" },
  { key: "chatbot", label: "Chatbot (Aria)" },
];

const LABELS = {
  hero: {
    headline_line_1: "Headline · Line 1",
    headline_line_2: "Headline · Line 2",
    subheadline: "Subheadline (paragraph)",
    cta_label: "CTA Button Label",
  },
  home_about: {
    subtitle: "Small subtitle above the section",
    title_line_1: "Title · Line 1",
    title_line_2: "Title · Line 2",
    title_line_3_prefix: "Line 3 · Prefix",
    title_line_3_emphasis: "Line 3 · Italic Emphasis Word",
    paragraph: "Paragraph (main body)",
  },
  market: {
    subtitle: "Small subtitle",
    title_line_1: "Title · Line 1",
    title_line_2_prefix: "Line 2 · Prefix",
    title_line_2_emphasis: "Line 2 · Italic Emphasis",
    title_line_2_suffix: "Line 2 · Suffix",
    description: "Description paragraph",
  },
  contact: {
    subtitle: "Small subtitle",
    office_line_1: "Office · Line 1",
    office_line_2: "Office · Line 2",
    phone_display: "Phone (display, spaced)",
    phone_raw: "Phone (raw digits for tel: link)",
    email: "Concierge Email",
  },
  footer: {
    tagline: "Footer tagline (paragraph)",
    rera_no: "RERA Number",
    copyright_line: "Copyright line",
    credit_line: "Credit line (right)",
  },
  chatbot: {
    greeting: "Opening greeting message",
    agent_name: "Agent name (e.g. Aria)",
    agent_title: "Agent title (e.g. AI Concierge)",
  },
};

export default function SiteContentPanel() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState("hero");

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminHttp.get("/site-content");
      setContent(r.data);
    } catch (e) {
      toast.error("Failed to load site content");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setField = (section, key, value) => {
    setContent({ ...content, [section]: { ...(content[section] || {}), [key]: value } });
  };

  const save = async () => {
    setBusy(true);
    try {
      await adminHttp.put("/admin/site-content", content);
      toast.success("Content saved. The site updates on next page load.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset all site content to factory defaults? Your edits will be lost.")) return;
    try {
      const r = await adminHttp.post("/admin/site-content/reset");
      setContent(r.data.content);
      toast.success("Reset to defaults.");
    } catch {
      toast.error("Reset failed");
    }
  };

  if (loading || !content) {
    return (
      <div className="flex items-center gap-2 text-[#6a6259] text-sm">
        <Loader2 size={14} className="animate-spin" /> Loading site content…
      </div>
    );
  }

  const currentSection = content[active] || {};
  const currentLabels = LABELS[active] || {};

  // Special sub-editor: home_about.pillars (list)
  const pillars = content.home_about?.pillars || [];
  const setPillar = (i, k, v) => {
    const next = [...pillars];
    next[i] = { ...next[i], [k]: v };
    setContent({ ...content, home_about: { ...content.home_about, pillars: next } });
  };
  const addPillar = () => {
    const n = String(pillars.length + 1).padStart(2, "0");
    setContent({ ...content, home_about: { ...content.home_about, pillars: [...pillars, { n, title: "", body: "" }] } });
  };
  const removePillar = (i) => {
    setContent({ ...content, home_about: { ...content.home_about, pillars: pillars.filter((_, idx) => idx !== i) } });
  };

  return (
    <div data-testid="admin-site-content-panel">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="underline-label mb-2">Copy & Voice</div>
          <h2 className="font-display text-3xl text-[#1c1c1c]">Site Content</h2>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="site-content-reset"
            onClick={reset}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#c5a059]/30 text-[#6a6259] uppercase text-[10px] tracking-[0.28em] hover:text-[#c5a059]"
          >
            <RotateCcw size={12} /> Reset
          </button>
          <button
            data-testid="site-content-save"
            onClick={save}
            disabled={busy}
            className="inline-flex items-center gap-3 px-6 py-2.5 bg-[#c5a059] text-black uppercase text-[10px] tracking-[0.32em] hover:bg-[#1c1c1c] hover:text-[#f5f0e8] disabled:opacity-60"
          >
            <Save size={12} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap gap-2 mb-8">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            data-testid={`sc-tab-${s.key}`}
            onClick={() => setActive(s.key)}
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.28em] border transition-all ${
              active === s.key
                ? "border-[#c5a059] bg-[#c5a059] text-black"
                : "border-[#c5a059]/25 text-[#3d2b1f]/70 hover:border-[#c5a059] hover:text-[#c5a059]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Main fields */}
      <div className="space-y-6 mb-12">
        {Object.entries(currentLabels).map(([key, label]) => {
          const val = currentSection[key] ?? "";
          const isLong = key === "paragraph" || key === "subheadline" || key === "description" || key === "tagline" || key === "greeting" || key === "body";
          return (
            <div key={key}>
              <div className="underline-label mb-2 text-[#6a6259]">{label}</div>
              {isLong ? (
                <textarea
                  data-testid={`sc-${active}-${key}`}
                  rows={4}
                  className="editorial-input resize-none"
                  value={val}
                  onChange={(e) => setField(active, key, e.target.value)}
                />
              ) : (
                <input
                  data-testid={`sc-${active}-${key}`}
                  className="editorial-input"
                  value={val}
                  onChange={(e) => setField(active, key, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Pillars sub-editor (only for home_about) */}
      {active === "home_about" && (
        <div className="border-t border-[#1c1c1c]/12 pt-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="underline-label mb-2">The Three Pillars</div>
              <div className="font-display text-xl text-[#1c1c1c]">Values shown under the About paragraph</div>
            </div>
            <button
              type="button"
              data-testid="sc-pillar-add"
              onClick={addPillar}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#c5a059]/50 text-[#c5a059] uppercase text-[10px] tracking-[0.28em] hover:bg-[#c5a059] hover:text-black"
            >
              <Plus size={12} /> Add Pillar
            </button>
          </div>
          <div className="space-y-6">
            {pillars.map((p, i) => (
              <div key={i} data-testid={`sc-pillar-${i}`} className="grid grid-cols-12 gap-4 p-4 border border-[#1c1c1c]/10 bg-white/70">
                <div className="col-span-2">
                  <div className="underline-label mb-2 text-[#6a6259]">Number</div>
                  <input
                    data-testid={`sc-pillar-${i}-n`}
                    className="editorial-input"
                    value={p.n}
                    onChange={(e) => setPillar(i, "n", e.target.value)}
                  />
                </div>
                <div className="col-span-3">
                  <div className="underline-label mb-2 text-[#6a6259]">Title</div>
                  <input
                    data-testid={`sc-pillar-${i}-title`}
                    className="editorial-input"
                    value={p.title}
                    onChange={(e) => setPillar(i, "title", e.target.value)}
                  />
                </div>
                <div className="col-span-6">
                  <div className="underline-label mb-2 text-[#6a6259]">Description</div>
                  <input
                    data-testid={`sc-pillar-${i}-body`}
                    className="editorial-input"
                    value={p.body}
                    onChange={(e) => setPillar(i, "body", e.target.value)}
                  />
                </div>
                <div className="col-span-1 flex items-end justify-end pb-1">
                  <button
                    type="button"
                    data-testid={`sc-pillar-${i}-remove`}
                    onClick={() => removePillar(i)}
                    className="p-2 text-[#6a6259] hover:bg-red-900 hover:text-white transition-colors"
                    aria-label="Remove pillar"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 text-[11px] text-[#6a6259] leading-relaxed">
        Changes save immediately to the database. The public site fetches this content on every
        page load — a browser refresh will show your edits. Timeline, milestones, CEO letter body,
        and market KPIs are available as an additional section on request.
      </div>
    </div>
  );
}
